import React, { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";

const PRESETS = [
  { id: "meta", label: "Meta Ads", source: "meta", medium: "paid_social" },
  { id: "tiktok", label: "TikTok Ads", source: "tiktok", medium: "paid_social" },
  { id: "google", label: "Google Ads", source: "google", medium: "paid_search" },
  { id: "social", label: "Organic Social", source: "instagram", medium: "organic_social" },
  { id: "search", label: "Organic Search", source: "google", medium: "organic" },
  { id: "email", label: "Email", source: "email", medium: "email" },
  { id: "referral", label: "Referral", source: "partner", medium: "referral" },
  { id: "custom", label: "مخصص", source: "", medium: "" },
];

const inputClass = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm";

const buildUrl = ({ destination, source, medium, campaign, campaignId, content, term }) => {
  const raw = String(destination || "").trim();
  if (!raw) return { error: "اكتب رابط الصفحة." };
  let url;
  try {
    url = new URL(raw);
  } catch {
    return { error: "الرابط غير صالح. لازم يبدأ بـ https:// أو http://." };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return { error: "الرابط لازم يكون http أو https." };
  const set = (key, value) => {
    const text = String(value || "").trim();
    url.searchParams.delete(key);
    if (text) url.searchParams.set(key, text);
  };
  set("utm_source", source);
  set("utm_medium", medium);
  set("utm_campaign", campaign);
  set("utm_id", campaignId);
  set("utm_content", content);
  set("utm_term", term);
  if (!url.searchParams.get("utm_source") || !url.searchParams.get("utm_medium")) {
    return { error: "المصدر والوسيط مطلوبين." };
  }
  return { url: url.toString() };
};

export default function TrackingLinkBuilder() {
  const [preset, setPreset] = useState("meta");
  const [destination, setDestination] = useState("https://www.girabat.com/");
  const [source, setSource] = useState("meta");
  const [medium, setMedium] = useState("paid_social");
  const [campaign, setCampaign] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [content, setContent] = useState("");
  const [term, setTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");

  const preview = useMemo(
    () => buildUrl({ destination, source, medium, campaign, campaignId, content, term }),
    [destination, source, medium, campaign, campaignId, content, term]
  );

  const applyPreset = (id) => {
    const next = PRESETS.find((item) => item.id === id) || PRESETS[0];
    setPreset(id);
    if (id !== "custom") {
      setSource(next.source);
      setMedium(next.medium);
    }
    setCopied(false);
    setMessage("");
  };

  const copy = async () => {
    if (!preview.url) {
      setMessage(preview.error || "تعذر إنشاء الرابط");
      setCopied(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(preview.url);
      setCopied(true);
      setMessage("تم نسخ الرابط");
    } catch {
      setCopied(false);
      setMessage("تعذر النسخ. انسخ الرابط من المعاينة.");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => applyPreset(item.id)}
            className={`rounded-lg px-2.5 py-1.5 text-xs font-medium ${preset === item.id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="md:col-span-2 text-xs text-slate-500">رابط الصفحة
          <input className={`${inputClass} mt-1`} value={destination} onChange={(e) => setDestination(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500">المصدر
          <input className={`${inputClass} mt-1`} value={source} onChange={(e) => { setSource(e.target.value); setPreset("custom"); }} />
        </label>
        <label className="text-xs text-slate-500">الوسيط
          <input className={`${inputClass} mt-1`} value={medium} onChange={(e) => { setMedium(e.target.value); setPreset("custom"); }} />
        </label>
        <label className="text-xs text-slate-500">اسم الحملة
          <input className={`${inputClass} mt-1`} value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="summer_sale" />
        </label>
        <label className="text-xs text-slate-500">معرّف الحملة
          <input className={`${inputClass} mt-1`} value={campaignId} onChange={(e) => setCampaignId(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500">المحتوى / الكريتيف
          <input className={`${inputClass} mt-1`} value={content} onChange={(e) => setContent(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500">الكلمة
          <input className={`${inputClass} mt-1`} value={term} onChange={(e) => setTerm(e.target.value)} />
        </label>
      </div>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs break-all text-slate-700" dir="ltr">
        {preview.url || preview.error}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-medium text-white">
          {copied ? <Check size={14} /> : <Copy size={14} />}
          نسخ الرابط
        </button>
        {message && <span className={`text-sm ${copied ? "text-emerald-700" : "text-rose-700"}`}>{message}</span>}
      </div>
    </div>
  );
}
